// src/pages/CheckForm.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { intervalLabel, intervalBadgeClass, formatDate, getErrorMessage, now8601 } from '../utils/helpers';
import { PageWrapper } from './Dashboard';

export default function CheckForm() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { staff } = useAuth();

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [checkOptions, setCheckOptions] = useState({ location: [], activity: [], mood: [] });
  const [sigPin, setSigPin] = useState('');
  const [showPin, setShowPin] = useState(false);

  const [form, setForm] = useState({
    checkDatetime: now8601(),
    location: '',
    activity: '',
    mood: '',
    notes: '',
    respiration: true
  });

  useEffect(() => {
    Promise.all([
      api.get(`/clients/${clientId}`),
      api.get('/check-options')
    ]).then(([clientRes, optionsRes]) => {
      const clientData = clientRes.data;
      setClient(clientData);
      const opts = optionsRes.data;
      setCheckOptions(opts);
      setForm(f => ({
        ...f,
        location: clientData.last_check_location || opts.location?.[0]?.label || '',
        activity: clientData.last_check_activity || opts.activity?.[0]?.label || '',
        mood: clientData.last_check_mood || opts.mood?.[0]?.label || '',
      }));
    }).catch(() => setError('Failed to load check form'))
    .finally(() => setLoading(false));
  }, [clientId]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!sigPin || sigPin.length < 6) { setError('Please enter your 6-digit PIN to sign this check.'); return; }
    setSubmitting(true); setError('');
    try {
      await api.post('/checks', { clientId, ...form, sigPin });
      navigate('/');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };



  if (loading) return <PageWrapper active="home"><div className="spinner" /></PageWrapper>;



  // Detox badge info
  const isInDetoxWindow = client?.isInDetoxWindow;
  const detoxHour = client?.detoxHoursElapsed;

  return (
    <PageWrapper active="home" title={`Check — ${client?.first_name || '...'} ${client?.last_name || ''}`}>
      <div style={{ maxWidth: 600 }}>
        {client && (
          <div className="card" style={{ marginBottom: 16, borderLeft: '3px solid var(--primary)', borderRadius: '0 12px 12px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 16 }}>{client.first_name} {client.last_name}</div>
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
                  DOB: {formatDate(client.date_of_birth)}
                  {client.room_number && ` · Room ${client.room_number}`}
                </div>
                {client.program_type && (
                  <div style={{ marginTop: 6 }}>
                    <span style={{
                      background: client.program_type === 'detox' ? '#FEF3C7' : '#EFF6FF',
                      color: client.program_type === 'detox' ? '#92400E' : '#1E40AF',
                      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                      border: `1px solid ${client.program_type === 'detox' ? '#FCD34D' : '#BFDBFE'}`
                    }}>
                      {client.program_type === 'detox'
                        ? (isInDetoxWindow ? `🔶 Detox · Hour ${detoxHour} of 72` : '🔶 Detox')
                        : '🏠 Residential'}
                    </span>
                  </div>
                )}
              </div>
              <span className={intervalBadgeClass(client.current_interval_minutes)}>
                {intervalLabel(client.current_interval_minutes)} checks
              </span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="card">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>Time of check</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="datetime-local"
                    name="checkDatetime"
                    value={form.checkDatetime}
                    onChange={e => {
                      const selected = new Date(e.target.value);
                      const now = new Date();
                      const fifteenAgo = new Date(now.getTime() - 15 * 60000);
                      if (selected > now) {
                        setError('Check time cannot be in the future.');
                        return;
                      }
                      if (selected < fifteenAgo) {
                        setError('Check time cannot be more than 15 minutes in the past.');
                        return;
                      }
                      setError('');
                      setForm(f => ({ ...f, checkDatetime: e.target.value }));
                    }}
                    required
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => { setError(''); setForm(f => ({ ...f, checkDatetime: now8601() })); }}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Set to now
                  </button>
                </div>
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>
                  You may log a check up to 15 minutes in the past.
                </div>
              </div>
              <div className="field">
                <label>Location</label>
                <select name="location" value={form.location} onChange={handleChange} required>
                  {checkOptions.location.map(l => <option key={l.id}>{l.label}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Mood / demeanor</label>
                <select name="mood" value={form.mood} onChange={handleChange} required>
                  {checkOptions.mood.map(m => <option key={m.id}>{m.label}</option>)}
                </select>
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>Activity / behavior observed</label>
                <select name="activity" value={form.activity} onChange={handleChange} required>
                  {checkOptions.activity.map(a => <option key={a.id}>{a.label}</option>)}
                </select>
              </div>

              {/* Respiration */}
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>Respiration observed</label>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, respiration: true }))}
                    style={{
                      flex: 1, padding: '10px 0', borderRadius: 8, border: '2px solid',
                      borderColor: form.respiration ? 'var(--primary)' : '#E5E7EB',
                      background: form.respiration ? 'var(--primary)' : '#fff',
                      color: form.respiration ? '#fff' : '#374151',
                      fontWeight: 600, fontSize: 14, cursor: 'pointer'
                    }}
                  >
                    ✓ Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, respiration: false }))}
                    style={{
                      flex: 1, padding: '10px 0', borderRadius: 8, border: '2px solid',
                      borderColor: !form.respiration ? '#DC2626' : '#E5E7EB',
                      background: !form.respiration ? '#DC2626' : '#fff',
                      color: !form.respiration ? '#fff' : '#374151',
                      fontWeight: 600, fontSize: 14, cursor: 'pointer'
                    }}
                  >
                    ✗ No
                  </button>
                </div>
                {!form.respiration && (
                  <div style={{ marginTop: 6, padding: '8px 12px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, fontSize: 12, color: '#B91C1C', fontWeight: 500 }}>
                    ⚠ No respiration observed — notify clinical staff immediately
                  </div>
                )}
              </div>

              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>Notes (optional)</label>
                {/* Decoy fields to absorb iOS Passwords autofill */}<input type="text" name="username" autoComplete="username" tabIndex={-1} aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} readOnly /><input type="password" name="password" autoComplete="current-password" tabIndex={-1} aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} readOnly /><textarea name="observation" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Additional observations, interventions, or concerns..." autoComplete="off" autoCorrect="off" autoCapitalize="sentences" spellCheck={true} data-form-type="other" data-lpignore="true" inputMode="text" />
              </div>
            </div>

            <hr className="divider" />

            {/* PIN-based electronic signature */}
            <div className="field">
              <label>Electronic signature</label>
              <div style={{ background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: '#4B5563', lineHeight: 1.7 }}>
                  By entering your PIN below, you attest that you personally observed <strong>{client?.first_name} {client?.last_name}</strong> at the time indicated and that the information recorded is accurate.
                </div>
              </div>
              <div style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 8 }}>
                Signing as: <strong style={{ color: 'var(--primary)' }}>{staff?.firstName} {staff?.lastName}</strong>
              </div>
              <div style={{ position: 'relative', maxWidth: 220 }}>
                <input
                  type={showPin ? 'text' : 'password'}
                  inputMode="numeric"
                  placeholder="Enter your 6-digit PIN"
                  value={sigPin}
                  onChange={e => setSigPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  autoComplete="one-time-code"
                  style={{ letterSpacing: '0.2em', paddingRight: 40 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPin(v => !v)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}
                >
                  {showPin ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                {sigPin.length}/6 digits {sigPin.length === 6 && '✓'}
              </div>
            </div>

            {error && <div className="alert-bar danger" style={{ marginTop: 8 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-primary" type="submit" disabled={submitting || sigPin.length < 6} style={{ flex: 1 }}>
                {submitting ? 'Submitting...' : 'Submit check'}
              </button>
              <button className="btn btn-secondary" type="button" style={{ width: 100 }} onClick={() => navigate('/')}>Cancel</button>
            </div>
          </div>
        </form>
      </div>
    </PageWrapper>
  );
}

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
